import React, { useState } from "react";
import {
  Box,
  Card,
  CardContent,
  TextField,
  Button,
  Typography,
  Container,
  Alert,
  InputAdornment,
  IconButton
} from "@mui/material";
import { Visibility, VisibilityOff, Lock, Code } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { useSetRecoilState } from "recoil";
import { adminAPI } from "../api/admin";
import { authTokenState, currentProjectState } from "../state/atoms";

const Login: React.FC = () => {
  const [projectCode, setProjectCode] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const navigate = useNavigate();
  const setAuthToken = useSetRecoilState(authTokenState);
  const setCurrentProject = useSetRecoilState(currentProjectState);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    try {
      const response = await adminAPI.login(projectCode, password);

      // Store token
      localStorage.setItem("adminToken", response.token);
      setAuthToken(response.token);
      setCurrentProject(response.project);

      // Navigate to dashboard
      navigate("/dashboard");
    } catch (err: any) {
      setError(
        err.response?.data?.error ||
          "Login failed. Please check your credentials."
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: `radial-gradient(circle at 50% 50%, rgba(255, 237, 0, 0.05) 0%, transparent 60%),
                     radial-gradient(circle at 20% 80%, rgba(255, 237, 0, 0.03) 0%, transparent 50%),
                     radial-gradient(circle at 80% 20%, rgba(61, 71, 78, 0.08) 0%, transparent 50%),
                     #192239`,
        py: { xs: 2, sm: 4 },
        px: { xs: 2, sm: 0 }
      }}
    >
      <Container maxWidth="sm">
        <Box sx={{ textAlign: "center", mb: 4 }}>
          <Typography
            variant="h3"
            component="h1"
            sx={{
              fontFamily: "var(--font-heading)",
              fontWeight: 600,
              background: "linear-gradient(135deg, #FFED00, #FFF27A)",
              backgroundClip: "text",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
              mb: 1,
              letterSpacing: "-0.02em"
            }}
          >
            WebPal
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ fontSize: "1.1rem" }}>
            Admin Dashboard
          </Typography>
        </Box>

        <Card
          elevation={0}
          sx={{
            borderRadius: 4,
            background: "linear-gradient(180deg, rgba(44, 47, 56, 0.95), rgba(44, 47, 56, 0.85))",
            border: "1px solid rgba(61, 71, 78, 0.3)",
            boxShadow: "0 20px 60px rgba(0, 0, 0, 0.5), 0 0 30px rgba(255, 237, 0, 0.1)",
            overflow: "hidden"
          }}
        >
          <CardContent sx={{ p: { xs: 3, sm: 5 } }}>
            <form onSubmit={handleLogin}>
              <Box sx={{ mb: 3 }}>
                <TextField
                  fullWidth
                  label="Project Code"
                  value={projectCode}
                  onChange={(e) => setProjectCode(e.target.value)}
                  required
                  variant="outlined"
                  placeholder="e.g., 12345678"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Code sx={{ color: "text.secondary" }} />
                      </InputAdornment>
                    )
                  }}
                  sx={{ 
                    mb: 2,
                    "& .MuiOutlinedInput-root": {
                      borderRadius: 2,
                      "& fieldset": {
                        borderColor: "rgba(255, 255, 255, 0.2)"
                      },
                      "&:hover fieldset": {
                        borderColor: "rgba(255, 237, 0, 0.5)"
                      },
                      "&.Mui-focused fieldset": {
                        borderColor: "#FFED00"
                      }
                    }
                  }}
                />

                <TextField
                  fullWidth
                  label="Password"
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  variant="outlined"
                  placeholder="Enter your password"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Lock sx={{ color: "text.secondary" }} />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setShowPassword(!showPassword)}
                          edge="end"
                          sx={{ color: "text.secondary" }}
                        >
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    )
                  }}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: 2,
                      "& fieldset": {
                        borderColor: "rgba(255, 255, 255, 0.2)"
                      },
                      "&:hover fieldset": {
                        borderColor: "rgba(255, 237, 0, 0.5)"
                      },
                      "&.Mui-focused fieldset": {
                        borderColor: "#FFED00"
                      }
                    }
                  }}
                />
              </Box>

              {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {error}
                </Alert>
              )}

              <Button
                type="submit"
                fullWidth
                variant="contained"
                size="large"
                disabled={loading}
                sx={{
                  py: 1.75,
                  borderRadius: 2,
                  textTransform: "none",
                  fontSize: "1rem",
                  fontWeight: 700,
                  background: "linear-gradient(135deg, #FFED00, #E8D400)",
                  color: "#192239",
                  boxShadow: "0 8px 18px rgba(255, 237, 0, 0.3), 0 0 20px rgba(255, 237, 0, 0.2)",
                  transition: "all 0.3s ease",
                  "&:hover": {
                    background: "linear-gradient(135deg, #FFF27A, #FFED00)",
                    boxShadow: "0 15px 30px rgba(255, 237, 0, 0.5), 0 0 30px rgba(255, 237, 0, 0.4)",
                    transform: "translateY(-2px)"
                  },
                  "&:disabled": {
                    background: "rgba(255, 237, 0, 0.2)",
                    color: "rgba(25, 34, 57, 0.5)"
                  }
                }}
              >
                {loading ? "Logging in..." : "Sign In"}
              </Button>
            </form>

            <Box sx={{ mt: 4, textAlign: "center" }}>
              <Typography 
                variant="body2" 
                sx={{ 
                  color: "text.secondary",
                  fontSize: "0.9rem",
                  lineHeight: 1.6
                }}
              >
                Enter your project code and password to access the admin portal
              </Typography>
            </Box>
          </CardContent>
        </Card>
      </Container>
    </Box>
  );
};

export default Login;